import _tmpl from "./tbid.html";
import { LightningElement, registerComponent as _registerComponent } from 'lwc';
import { UserManager } from 'oidc-client';
import { clearAuthenticationContext, getAuthenticationContext } from '~/modules/analytics/authenticationContext';
import { NODE_ENV, TBID_CONSUMER_KEY, TBID_URL } from '~/modules/config';
import { generateBaseUrl, generateTbidLoginUrl, generateTbidSignUpUrl } from '~/modules/helpers/routingHelper';
import { Routes } from '~/modules/routes';
import { isLoggedIn } from '~/modules/services/authService';
import { userStore } from '~/modules/stores/userStore';
import { appendMetaTag, removeMetaTag } from '~/modules/utilities/browser';
import { createCookie } from '~/modules/utilities/cookies';
import { logger } from '~/modules/utilities/logger';

// authentication is expected to be OpenID Connect with TBID
//
// this module wraps the oidc client we're using and implements functions required for
// the TBID-provided authProviderEmbeddedLogin_v1_3 library
const userManagerSettings = {
  authority: TBID_URL,
  client_id: TBID_CONSUMER_KEY,
  redirect_uri: `${generateBaseUrl()}/${Routes.CALLBACK}`,
  response_type: 'code',
  scope: 'openid profile',
  filterProtocolClaims: true,
  loadUserInfo: false,
  mergeClaims: true
};
const userManager = new UserManager(userManagerSettings);

// name of functions added in connected callback and used by the
// TBID auth widget to communicate with this component
const TBID_INIT_HANDLER = 'onInitHandler';
const TBID_LOGIN_HANDLER = 'onLoginHandler';
const TBID_LOGOUT_HANDLER = 'onLogoutHandler';

// the TBID widget path - this hasn't changed since TBID originally
// went live but maybe it will change someday
const TBID_AUTH_WIDGET_PATH = '/resource/authProviderEmbeddedLogin_v1_3';
class Tbid extends LightningElement {
  connectedCallback() {
    appendMetaTag('salesforce-cache-max-age', '0');
    appendMetaTag('salesforce-brand', 'AppExchange Today');
    appendMetaTag('salesforce-community', TBID_URL);
    appendMetaTag('salesforce-mode', 'authprovider');
    appendMetaTag('salesforce-redirect-uri', `${generateBaseUrl()}/${Routes.CALLBACK}`);
    appendMetaTag('salesforce-authprovider-login', generateTbidLoginUrl());
    appendMetaTag('salesforce-authprovider-signup', generateTbidSignUpUrl());
    appendMetaTag('salesforce-init-handler', TBID_INIT_HANDLER);
    appendMetaTag('salesforce-login-handler', TBID_LOGIN_HANDLER);
    appendMetaTag('salesforce-logout-handler', TBID_LOGOUT_HANDLER);
    loadSFID(TBID_URL + TBID_AUTH_WIDGET_PATH).catch(error => {
      logger.error(`Failed to load TBID auth widget ${error}`);
    });
  }
  disconnectedCallback() {
    removeMetaTag('salesforce-cache-max-age');
    removeMetaTag('salesforce-brand');
    removeMetaTag('salesforce-community');
    removeMetaTag('salesforce-mode');
    removeMetaTag('salesforce-redirect-uri');
    removeMetaTag('salesforce-authprovider-login');
    removeMetaTag('salesforce-authprovider-signup');
    removeMetaTag('salesforce-init-handler');
    removeMetaTag('salesforce-login-handler');
    removeMetaTag('salesforce-logout-handler');
  }
  /*LWC compiler v7.1.3*/
}
const __lwc_component_class_internal = _registerComponent(Tbid, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;

// Global handlers need to be writable for tests
Object.defineProperty(window, TBID_INIT_HANDLER, {
  value: onInitHandler,
  writable: NODE_ENV === 'test'
});
Object.defineProperty(window, TBID_LOGIN_HANDLER, {
  value: onLoginHandler,
  writable: NODE_ENV === 'test'
});
Object.defineProperty(window, TBID_LOGOUT_HANDLER, {
  value: onLogoutHandler,
  writable: NODE_ENV === 'test'
});
let tbidTimeoutId;
let autoLoginFrame;
function onInitHandler() {
  /*
  At the user state we have the isLoading flag to indicate whether all "required" user information was fetched or not 
  This method is called by TBID library (authProviderEmbeddedLogin_v1_3) for Guest User navigation 
  And since we currently do not need to fetch any additional information we are indicating that the UserState its all set 
  */
  if (tbidTimeoutId) {
    clearTimeout(tbidTimeoutId);
  }
  userStore.setLoading(false);
}
async function onLoginHandler(userInfo) {
  if (tbidTimeoutId) {
    clearTimeout(tbidTimeoutId);
  }
  if (!userInfo?.active) {
    logger.warn('Inactive user logged in');
    userStore.setGuestUser();
    return;
  }
  if (!userStore.getState().isLoading) {
    logger.warn('We already loaded the profile, ignore TBID login handler');
    return;
  }
  userStore.setTbidProfile(userInfo);
  const {
    origin,
    type
  } = getAuthenticationContext();
  // @ts-expect-error * Automated Type Error Fix
  userStore.setAuthenticationFlow(type, origin);
  const user = await isLoggedIn();
  if (user?.isGuestUser === false) {
    userStore.setOrgUserInfo(user);
    return;
  }

  // Looks like we have a TBID user, but an invalid or missing sid cookie.
  // We need to auto-login the user inside an iframe and refresh the page when it completes.
  if (autoLoginFrame) {
    return;
  }
  autoLoginFrame = document.createElement('iframe');
  autoLoginFrame.src = generateTbidLoginUrl();
  autoLoginFrame.setAttribute('style', 'width: 0px; height: 0px; position: absolute');
  document.body.appendChild(autoLoginFrame);

  // The following code is largely copied from:
  // https://git.soma.salesforce.com/appexchange/tz-ecosystem/blob/develop/TrailblazerIdentity/static/assets-tbi/js/tbid-header.js#L15
  autoLoginFrame.addEventListener('load', async () => {
    if (autoLoginFrame.getAttribute('data-has-refreshed') !== 'true') {
      // Set a data attribute to avoid recursive page loading if src is set for whatever reason.
      autoLoginFrame.setAttribute('data-has-refreshed', 'true');
      const autoLoggedInUser = await isLoggedIn();
      if (autoLoggedInUser?.isGuestUser === false) {
        userStore.setOrgUserInfo(autoLoggedInUser);
        // Refresh the page to avoid running into errors with Javascript remoting when the session changes.
        window.location.reload();
      } else {
        userStore.setGuestUser();
      }
    }
  });
}
function onLogoutHandler() {
  logger.info('onLogoutHandler INVOKED');
  userStore.reset();
  clearAuthenticationContext();
  //invalidate the org sid cookie if it exists
  createCookie('sid', '', -1);
  // TODO: Why is this here?
  window.SFIDWidget.init();
}
function loadSFID(src) {
  const load = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.onload = () => {
      tbidTimeoutId = setTimeout(() => {
        // TODO: This needs to be a logger call - so it makes it to websocket logger
        console.log('TBID didnt run, forcing init call');
        onInitHandler();
      }, 3000);
      resolve(undefined);
    };
    script.onerror = () => reject(new Error('TBID_LOAD_ERROR'));
    script.async = false;
    script.src = src;
    document.head.appendChild(script);
  });
  const timeout = new Promise((_, reject) => setTimeout(reject, 5000, new Error('TBID_LOAD_TIMEOUT')));
  return Promise.race([load, timeout]);
}